import axios from "axios";

// Pick the right backend origin at RUNTIME. Priority:
//   1. `window.location.origin` when it looks like a production deploy
//      (i.e. not a *.emergentagent.com preview URL) — this keeps every
//      request same-origin so the browser skips the CORS preflight
//      entirely. Same-origin also means the session cookie is always
//      attached correctly.
//   2. `REACT_APP_BACKEND_URL` from the build-time env otherwise (dev,
//      preview, or when the app is embedded on another host).
//
// This resilience matters on lendlyneighbor.com — if the deploy pipeline
// forgets to overwrite REACT_APP_BACKEND_URL, requests would otherwise be
// blocked by CORS because the browser can't send credentials to a wildcard
// preflight origin.
const _origin = typeof window !== "undefined" ? window.location.origin : "";
const _envBackend = process.env.REACT_APP_BACKEND_URL || "";
const _isPreviewHost = /\.(emergentagent|emergent)\.com$/i.test(
    _origin ? new URL(_origin).hostname : "",
);
const BACKEND_URL =
    _origin && !_isPreviewHost && _origin !== _envBackend
        ? _origin
        : _envBackend;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// If the server says we're no longer authenticated, fire a window event
// so the AuthContext can clear its cached user and the UI updates.
api.interceptors.response.use(
    (resp) => resp,
    (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/signup")) {
            window.dispatchEvent(new CustomEvent("lendly:auth-expired"));
        }
        return Promise.reject(err);
    },
);

export const fetchMe = async () => {
    const { data } = await api.get("/auth/me");
    return data;
};

export const updateMyProfile = async (patch) => {
    const { data } = await api.patch("/auth/profile", patch);
    return data;
};

export const fetchMyReviews = async (userId) => {
    const { data } = await api.get(`/users/${userId}/reviews`);
    return data;
};

export const signupWithEmail = async ({ name, email, password }) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    return data;
};

export const loginWithEmail = async ({ email, password }) => {
    const { data } = await api.post("/auth/login", { email, password });
    return data;
};

export const completeProfile = async ({ address, zipCode, acceptTerms }) => {
    const { data } = await api.post("/auth/profile", {
        address,
        zip_code: zipCode,
        accept_terms: acceptTerms,
    });
    return data;
};

export const signOut = async () => {
    const { data } = await api.post("/auth/logout");
    return data;
};

export const requestPasswordReset = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
};

export const resetPassword = async ({ token, newPassword }) => {
    const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
    });
    return data;
};

export const fetchItems = async (params = {}) => {
    const clean = {};
    Object.entries(params).forEach(([k, v]) => {
        if (v === "" || v === undefined || v === null) return;
        if (k === "category" && v === "all") return;
        clean[k] = v;
    });
    const { data } = await api.get("/items", { params: clean });
    return data;
};

export const fetchItemsMeta = async () => {
    const { data } = await api.get("/items/meta");
    return data;
};

export const createItem = async (payload) => {
    const { data } = await api.post("/items", payload);
    return data;
};

export const suggestItemPrice = async ({ name, category, condition }) => {
    const { data } = await api.post("/items/price-suggestion", {
        name,
        category,
        condition,
    });
    return data;
};

export const uploadImage = async (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
    });
    // url returned is "/api/files/..." — make it absolute so <img src> works directly
    const absoluteUrl = `${BACKEND_URL}${data.url}`;
    return { ...data, url: absoluteUrl };
};

// --- Stripe checkout ---
export const createCheckoutSession = async ({ rentalId, originUrl }) => {
    const { data } = await api.post("/payments/checkout/session", {
        rental_id: rentalId,
        origin_url: originUrl,
    });
    return data;
};

// --- Rental requests (approval workflow) ---
export const requestRental = async ({ itemId, startDate, endDate, message }) => {
    const { data } = await api.post("/rentals/request", {
        item_id: itemId,
        start_date: startDate,
        end_date: endDate,
        message,
    });
    return data;
};

export const acceptRentalRequest = async (rentalId) => {
    const { data } = await api.post(`/rentals/${rentalId}/accept`);
    return data;
};

export const declineRentalRequest = async (rentalId) => {
    const { data } = await api.post(`/rentals/${rentalId}/decline`);
    return data;
};

export const cancelRentalRequest = async (rentalId) => {
    const { data } = await api.post(`/rentals/${rentalId}/cancel`);
    return data;
};

export const returnRental = async (rentalId, { conditionNote, returnPhotos } = {}) => {
    const { data } = await api.post(`/rentals/${rentalId}/return`, {
        condition_note: conditionNote || null,
        return_photos: returnPhotos && returnPhotos.length ? returnPhotos : null,
    });
    return data;
};

export const acceptReturn = async (rentalId) => {
    const { data } = await api.post(`/rentals/${rentalId}/accept-return`);
    return data;
};

export const fetchRecentReturns = async () => {
    const { data } = await api.get("/users/me/recent-returns");
    return data;
};

export const releaseDeposit = async (rentalId) => {
    const { data } = await api.post(`/rentals/${rentalId}/release-deposit`);
    return data;
};

export const withholdDeposit = async (rentalId, reason) => {
    const { data } = await api.post(`/rentals/${rentalId}/withhold-deposit`, {
        reason,
    });
    return data;
};

// --- Disputes ---
export const openDispute = async (rentalId, message) => {
    const { data } = await api.post(`/rentals/${rentalId}/dispute`, { message });
    return data;
};

export const fetchDisputeForRental = async (rentalId) => {
    const { data } = await api.get(`/rentals/${rentalId}/dispute`);
    return data;
};

export const askItemQuestion = async (itemId, { subject, body }) => {
    const { data } = await api.post(`/items/${itemId}/ask`, {
        subject: subject || null,
        body,
    });
    return data;
};

// --- Threaded Q&A ---
export const fetchQuestionThread = async (qid) => {
    const { data } = await api.get(`/item-questions/${qid}`);
    return data;
};

export const replyToQuestion = async (qid, body) => {
    const { data } = await api.post(`/item-questions/${qid}/messages`, { body });
    return data;
};

export const fetchMyQuestions = async () => {
    const { data } = await api.get("/users/me/questions");
    return data;
};

export const fetchMyDisputes = async () => {
    const { data } = await api.get("/users/me/disputes");
    return data;
};

export const postDisputeMessage = async (disputeId, body) => {
    const { data } = await api.post(`/disputes/${disputeId}/messages`, { body });
    return data;
};

export const resolveDispute = async (disputeId, resolutionNote) => {
    const { data } = await api.post(`/disputes/${disputeId}/resolve`, {
        resolution_note: resolutionNote || null,
    });
    return data;
};

export const fetchIncomingRequests = async () => {
    const { data } = await api.get("/users/me/incoming-requests");
    return data;
};

// --- In-app notifications (bell) ---
export const fetchMyNotifications = async () => {
    const { data } = await api.get("/users/me/notifications");
    return data;
};

export const markNotificationsRead = async (ids) => {
    const { data } = await api.post("/users/me/notifications/mark-read", {
        ids: ids || null,
    });
    return data;
};

export const getPaymentStatus = async (sessionId) => {
    const { data } = await api.get(`/payments/status/${sessionId}`);
    return data;
};

// --- Renter dashboard ---
export const fetchMyRentals = async () => {
    const { data } = await api.get("/users/me/rentals");
    return data;
};

// --- Seller dashboard ---
export const fetchMyListings = async () => {
    const { data } = await api.get("/users/me/listings");
    return data;
};

export const fetchMyAnalytics = async () => {
    const { data } = await api.get("/users/me/analytics");
    return data;
};

export const updateListing = async (itemId, payload) => {
    const { data } = await api.patch(`/items/${itemId}`, payload);
    return data;
};

export const updateListingStatus = async (itemId, status) => {
    const { data } = await api.patch(`/items/${itemId}/status`, { status });
    return data;
};

export const deleteListing = async (itemId) => {
    const { data } = await api.delete(`/items/${itemId}`);
    return data;
};

// --- Availability / blocked dates ---
export const fetchItemAvailability = async (itemId) => {
    const { data } = await api.get(`/items/${itemId}/availability`);
    return data;
};

export const updateBlockedDates = async (itemId, dates) => {
    const { data } = await api.put(`/items/${itemId}/blocked-dates`, {
        blocked_dates: dates,
    });
    return data;
};

// --- Neighbor profile & reviews ---
export const fetchNeighborProfile = async (userId) => {
    const { data } = await api.get(`/neighbors/${userId}`);
    return data;
};

export const submitReview = async (rentalId, { rating, comment }) => {
    const { data } = await api.post(`/rentals/${rentalId}/review`, {
        rating,
        comment,
    });
    return data;
};

// -------- Admin (email-allowlisted, permanent) --------
export const fetchAdminMe = async () => {
    const { data } = await api.get("/admin/me");
    return data;
};

export const fetchAdminStats = async () => {
    const { data } = await api.get("/admin/stats");
    return data;
};

export const fetchAdminUsers = async () => {
    const { data } = await api.get("/admin/users");
    return data;
};

export const adminSearchItems = async ({ q = "", owner = "" } = {}) => {
    const params = {};
    if (q) params.q = q;
    if (owner) params.owner = owner;
    const { data } = await api.get("/admin/items/search", { params });
    return data;
};

export const adminDeleteItem = async (itemId) => {
    const { data } = await api.delete(`/admin/items/${itemId}`);
    return data;
};

export const adminGenerateResetLink = async (email) => {
    const { data } = await api.post("/admin/users/reset-link", { email });
    return data;
};

export const adminResetMarketplace = async (confirmation) => {
    const { data } = await api.post("/admin/reset-marketplace", { confirmation });
    return data;
};



// -------- Stripe Connect (Express) — automatic owner payouts --------
export const fetchPayoutStatus = async () => {
    const { data } = await api.get("/payouts/status");
    return data;
};

export const startPayoutOnboarding = async () => {
    const { data } = await api.post("/payouts/onboarding-link", {
        origin_url: window.location.origin,
    });
    return data;
};

export const openPayoutDashboard = async () => {
    const { data } = await api.post("/payouts/dashboard-link");
    return data;
};

export const fetchPayoutBalance = async () => {
    const { data } = await api.get("/payouts/balance");
    return data;
};

export const withdrawPayoutBalance = async () => {
    const { data } = await api.post("/payouts/withdraw");
    return data;
};
